import {
  type FocusEvent,
  type KeyboardEvent,
  type ReactNode,
  useMemo,
  useState,
} from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import {
  MOCK_COMPANY_POOL,
  MOCK_FEATURED_COMPANIES,
} from '../../_mock-data/library'
import { useCoachingStore } from '../../store'
import { fuzzyMatch } from '../_lib/fuzzy'

type CompanyMode = 'include' | 'exclude' | undefined

const COMPANY_RESULTS_ID = 'company-search-results'

const CHIP_STYLES = {
  off: {
    background: 'transparent',
    borderColor: 'var(--cd-border-2)',
    color: 'var(--cd-text-muted)',
    textDecoration: 'none',
  },
  include: {
    background: 'color-mix(in oklab, var(--cd-green) 10%, transparent)',
    borderColor: 'color-mix(in oklab, var(--cd-green) 40%, transparent)',
    color: 'var(--cd-green)',
    textDecoration: 'none',
  },
  exclude: {
    background: 'color-mix(in oklab, var(--cd-red) 10%, transparent)',
    borderColor: 'color-mix(in oklab, var(--cd-red) 40%, transparent)',
    color: 'var(--cd-red)',
    textDecoration: 'line-through',
  },
} as const

const normalized = (company: string): string => company.toLowerCase()

const companyOptionId = (company: string): string =>
  `company-search-option-${normalized(company).replace(/[^a-z0-9]+/g, '-')}`

const chipLabel = (company: string, mode: CompanyMode): string => {
  if (mode === 'include') return `${company} ✓`
  if (mode === 'exclude') return `${company} ✕`
  return company
}

const chipTitle = (company: string, mode: CompanyMode): string => {
  if (mode === 'include') return `${company} included; click to exclude`
  if (mode === 'exclude') return `${company} excluded; click to turn off`
  return `Click to include ${company}`
}

export function CompanyTags(): ReactNode {
  const { curriculum } = useCoachingStore()
  const { companyTags, cycleCompanyTag } = curriculum
  const [query, setQuery] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const modeByCompany = useMemo(
    () =>
      new Map(
        companyTags.map((tag) => [normalized(tag.company), tag.mode] as const),
      ),
    [companyTags],
  )

  const shownCompanies = useMemo(() => {
    const featured = new Set(MOCK_FEATURED_COMPANIES.map(normalized))
    const extras = companyTags
      .map((tag) => tag.company)
      .filter((company) => !featured.has(normalized(company)))
    return [...MOCK_FEATURED_COMPANIES, ...extras]
  }, [companyTags])

  const matches = useMemo(() => {
    const shown = new Set(shownCompanies.map(normalized))
    return fuzzyMatch(
      query,
      MOCK_COMPANY_POOL.filter(
        (company) => !shown.has(normalized(company.name)),
      ),
    ).slice(0, 6)
  }, [query, shownCompanies])

  const listboxOpen = searchOpen && query.trim().length > 0
  const activeCompany = activeIndex === null ? undefined : matches[activeIndex]

  const changeQuery = (value: string): void => {
    setQuery(value)
    setActiveIndex(null)
    setSearchOpen(value.trim().length > 0)
  }

  const pickCompany = (company: string): void => {
    cycleCompanyTag(company)
    setQuery('')
    setActiveIndex(null)
    setSearchOpen(false)
  }

  const moveActive = (direction: 1 | -1): void => {
    if (query.trim() === '' || matches.length === 0) {
      setActiveIndex(null)
      return
    }

    setSearchOpen(true)
    setActiveIndex((current) => {
      if (current === null) return direction === 1 ? 0 : matches.length - 1
      return (current + direction + matches.length) % matches.length
    })
  }

  const handleSearchKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveActive(1)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveActive(-1)
      return
    }
    if (event.key === 'Enter' && listboxOpen && activeCompany) {
      event.preventDefault()
      pickCompany(activeCompany.name)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setActiveIndex(null)
      setSearchOpen(false)
    }
  }

  const handleSearchBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return
    }

    setActiveIndex(null)
    setSearchOpen(false)
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[9px] font-bold tracking-[.07em] text-(--cd-text-ghost)">
        COMPANIES
      </span>
      {shownCompanies.map((company) => {
        const mode = modeByCompany.get(normalized(company))
        return (
          <Tooltip key={normalized(company)}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => cycleCompanyTag(company)}
                className="cursor-pointer whitespace-nowrap rounded-full border px-3 py-0.5 text-[10px] font-semibold"
                style={CHIP_STYLES[mode ?? 'off']}
              >
                {chipLabel(company, mode)}
              </button>
            </TooltipTrigger>
            <TooltipContent>{chipTitle(company, mode)}</TooltipContent>
          </Tooltip>
        )
      })}
      <span className="whitespace-nowrap text-[9.5px] text-(--cd-text-ghost)">
        click: include · again: exclude · again: off
      </span>
      <div className="relative ml-auto" onBlur={handleSearchBlur}>
        <input
          type="search"
          role="combobox"
          aria-label="Other company"
          aria-autocomplete="list"
          aria-controls={COMPANY_RESULTS_ID}
          aria-expanded={listboxOpen}
          aria-activedescendant={
            listboxOpen && activeCompany
              ? companyOptionId(activeCompany.name)
              : undefined
          }
          value={query}
          onChange={(event) => changeQuery(event.target.value)}
          onFocus={() => {
            if (query.trim() !== '') setSearchOpen(true)
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder="+ other company…"
          className="w-[130px] rounded-full border border-(--cd-border) bg-(--cd-bg) px-3 py-1 text-[10px] text-(--cd-text) outline-none placeholder:text-(--cd-text-faint) focus:border-(--cd-border-3)"
        />
        {listboxOpen ? (
          <div
            id={COMPANY_RESULTS_ID}
            role="listbox"
            aria-label="Company search results"
            className="absolute top-[26px] right-0 z-45 min-w-[185px] rounded-[10px] border border-(--cd-border-4) bg-(--cd-panel) p-1 shadow-[0_12px_32px_rgba(0,0,0,.5)]"
          >
            {matches.length > 0 ? (
              matches.map((company, index) => (
                <button
                  key={company.name}
                  id={companyOptionId(company.name)}
                  type="button"
                  role="option"
                  aria-selected={activeIndex === index}
                  tabIndex={-1}
                  onClick={() => pickCompany(company.name)}
                  className={`flex w-full cursor-pointer items-center gap-2 rounded-md border-0 px-2 py-1 text-left text-[11px] hover:bg-(--cd-tab-active) hover:text-(--cd-text) ${activeIndex === index ? 'bg-(--cd-tab-active) text-(--cd-text)' : 'bg-transparent text-(--cd-text-2)'}`}
                >
                  <span>{company.name}</span>
                  <span className="ml-auto whitespace-nowrap text-[9px] text-(--cd-text-ghost)">
                    {company.count} questions
                  </span>
                </button>
              ))
            ) : (
              <span className="block px-2 py-1 text-[10px] text-(--cd-text-faint)">
                No matching company
              </span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  )
}
